/* Card quantity picker — DarePouch (ops/volume-tiers/)
   Web Component qui synchronise les radios (1/5/10), le stepper +/− et la quantité
   cachée envoyée au <product-form> du thème (AJAX cart natif via <quick-add-to-cart>).

   Le prix affiché reproduit le panier Shopify au centime, quelle que soit la quantité :
   - le barème (data-ladder-q / data-ladder-d) est celui des remises automatiques Shopify,
     posé dans le métachamp darepouch.volume_tiers par sync_tiers.py ;
   - le palier atteint = le plus haut seuil ≤ quantité (35 boîtes → palier 30) ;
   - la remise est calculée PAR BOÎTE et tronquée au centime, puis × quantité
     (4,90 € − 22 % → 4,90 − 1,07 = 3,83 € la boîte ; Shopify n'arrondit pas la ligne).
   Vérifié contre l'API Storefront (truth_check.py) et testé dans tests/test_card.mjs.
   Les montants sont écrits en euros au format de la boutique ; le convertisseur de devise
   (darepouch-currency-display.js) les réécrit ensuite en GBP/USD/CAD/AUD.

   Prix de tête de la carte = prix PAR CAN de la quantité choisie ([data-dp-percan]).
   Quantité de départ = test A/B (snippets/dp-qty-ab.liquid pose <html data-dp-qd="1|10">) :
   groupe « 1 » → 1, groupe « 10 » → data-default-qty (10, plafonné au stock). Le HTML porte
   déjà l'état des deux groupes : au démarrage, on garde la bonne version du prix de tête au
   lieu de la récrire, pour ne pas défaire une conversion de devise déjà faite. */

if (typeof CardQtyPicker !== 'function') {
  class CardQtyPicker extends HTMLElement {
    connectedCallback() {
      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.comparePrice = parseInt(this.dataset.comparePrice, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.maxQty = parseInt(this.dataset.maxQty, 10) || 9999;
      this.isoSuffix = this.dataset.isoSuffix || '';
      this.perCanLabel = this.dataset.perCan || '';
      this.ladder = CardQtyPicker.parseLadder(this.dataset.ladderQ, this.dataset.ladderD);

      this.tiers = Array.from(this.querySelectorAll('.card-qty-picker__tier'));
      this.radios = this.tiers.map((t) => t.querySelector('input[type="radio"]'));
      this.steps = Array.from(this.querySelectorAll('.card-qty-picker__step'));
      this.qtyInput = this.querySelector('.card-qty-picker__qty');
      this.hiddenQty = this.querySelector('[data-qty-input]');
      this.minus = this.querySelector('.card-qty-picker__minus');
      this.plus = this.querySelector('.card-qty-picker__plus');
      this.cardPriceEl = this.closest('.product-item')?.querySelector(
        '.product-item__price .product-price--original'
      );
      this.cardComparePriceEl = this.closest('.product-item')?.querySelector(
        '.product-item__price .product-price--compare'
      );
      this.perCanEl = this.closest('.product-item')?.querySelector('[data-dp-percan]');

      this._bindEvents();
      this._sync(CardQtyPicker.startQty(this.dataset.defaultQty, this.maxQty), { initial: true });
    }

    /* Groupe du test A/B : « 1 » ou « 10 » (sans attribut : « 10 », comme le CSS). */
    static group() {
      return document.documentElement.getAttribute('data-dp-qd') === '1' ? '1' : '10';
    }

    static startQty(defaultQty, maxQty) {
      if (CardQtyPicker.group() === '1') return 1;
      const q = parseInt(defaultQty, 10) || 1;
      return Math.max(1, Math.min(maxQty || 9999, q));
    }

    /* "5,10,20,30" + "7,10,17,22" → [{min:5,pct:7}, …] trié par seuil croissant. */
    static parseLadder(qStr, dStr) {
      const qs = String(qStr || '').split(',').map((s) => parseInt(s, 10));
      const ds = String(dStr || '').split(',').map((s) => parseInt(s, 10));
      const out = [];
      for (let i = 0; i < qs.length; i++) {
        if (Number.isFinite(qs[i]) && qs[i] > 0 && Number.isFinite(ds[i]) && ds[i] >= 0) {
          out.push({ min: qs[i], pct: ds[i] });
        }
      }
      return out.sort((a, b) => a.min - b.min);
    }

    /* Palier atteint pour une quantité : le plus haut seuil ≤ qty (0 % sous le premier). */
    static tierFor(ladder, qty) {
      let pct = 0;
      let min = 0;
      for (const t of ladder) {
        if (qty >= t.min) { pct = t.pct; min = t.min; }
      }
      return { pct, min };
    }

    /* Prix de ligne tel que Shopify le calcule : remise par unité tronquée au centime. */
    static linePriceCents(unitCents, qty, pct) {
      const unitDisc = Math.floor((unitCents * pct) / 100);
      return (unitCents - unitDisc) * qty;
    }

    _bindEvents() {
      this.radios.forEach((r) =>
        r.addEventListener('change', () => {
          if (r.checked) this._sync(parseInt(r.value, 10) || 1, { fromRadio: true });
        })
      );
      this.plus?.addEventListener('click', (e) => {
        e.preventDefault();
        this._sync(Math.min(this.maxQty, this._getQty() + 1));
      });
      this.minus?.addEventListener('click', (e) => {
        e.preventDefault();
        this._sync(Math.max(1, this._getQty() - 1));
      });
      this.qtyInput?.addEventListener('input', () => {
        const raw = this.qtyInput.value.replace(/[^0-9]/g, '');
        const q = parseInt(raw, 10);
        if (Number.isFinite(q) && q > 0) this._sync(Math.min(this.maxQty, q));
      });
      this.qtyInput?.addEventListener('blur', () => {
        if (!this.qtyInput.value || parseInt(this.qtyInput.value, 10) < 1) this._sync(1);
      });
    }

    _getQty() {
      return parseInt(this.qtyInput?.value, 10) || 1;
    }

    _sync(qty, opts = {}) {
      qty = Math.max(1, Math.min(this.maxQty, Math.floor(qty)));
      if (this.qtyInput) this.qtyInput.value = String(qty);
      if (this.hiddenQty) this.hiddenQty.value = String(qty);

      // Radio active = le raccourci le plus haut ≤ quantité (un simple repère visuel).
      let activeIdx = 0;
      this.radios.forEach((r, i) => {
        const v = parseInt(r.value, 10);
        if (qty >= v) activeIdx = i;
      });
      this.radios.forEach((r, i) => {
        const isActive = i === activeIdx;
        r.checked = isActive;
        this.tiers[i].classList.toggle('is-active', isActive);
      });

      // Palier réel : celui du barème Shopify, pas celui de la radio.
      const tier = CardQtyPicker.tierFor(this.ladder, qty);
      this.steps.forEach((el) => {
        const min = parseInt(el.dataset.min, 10) || 0;
        el.classList.toggle('is-active', min === tier.min && tier.pct > 0);
        el.classList.toggle('is-reached', min <= qty);
      });

      if (this.perCanEl) {
        this._renderPerCan(tier.pct, opts.initial);
      } else {
        this._renderTotal(qty, tier.pct);
      }

      if (this.plus) this.plus.disabled = qty >= this.maxQty;
      if (this.minus) this.minus.disabled = qty <= 1;
    }

    /* Prix de tête = total de la ligne (cartes sans [data-dp-percan]). */
    _renderTotal(qty, pct) {
      const naturalCents = this.unitPrice * qty;
      const mainCents = CardQtyPicker.linePriceCents(this.unitPrice, qty, pct);
      const compareCents = this.comparePrice > this.unitPrice ? this.comparePrice * qty : 0;
      // Barré = la référence la plus haute (prix barré catalogue avant prix naturel)
      const struckCents = Math.max(compareCents, pct > 0 ? naturalCents : 0);

      if (this.cardPriceEl) {
        this.cardPriceEl.textContent = this._formatMoney(mainCents);
      }

      if (struckCents > mainCents) {
        if (!this.cardComparePriceEl && this.cardPriceEl) {
          this.cardComparePriceEl = document.createElement('del');
          this.cardComparePriceEl.className = 'product-price--compare card-qty-picker__compare-injected';
          this.cardPriceEl.insertAdjacentElement('afterend', this.cardComparePriceEl);
        }
        if (this.cardComparePriceEl) {
          this.cardComparePriceEl.style.display = '';
          this.cardComparePriceEl.textContent = this._formatMoney(struckCents);
        }
      } else if (this.cardComparePriceEl) {
        this.cardComparePriceEl.style.display = 'none';
      }
    }

    /* Prix de tête = prix par can au palier atteint, barré sur le prix catalogue (ou le prix sans palier). */
    _renderPerCan(pct, initial) {
      const eachCents = this.unitPrice - Math.floor((this.unitPrice * pct) / 100);
      const refCents = this.comparePrice > this.unitPrice ? this.comparePrice : (pct > 0 ? this.unitPrice : 0);
      const key = eachCents + '|' + refCents;
      if (key === this._perCanKey) return;   // même palier : rien à récrire (et rien à reconvertir)

      const kept = initial && this.perCanEl.querySelector(`[data-dp-qd-for="${CardQtyPicker.group()}"]`);
      if (kept) {
        // Démarrage : la version du groupe est déjà dans le HTML, au bon prix (peut-être déjà convertie).
        this.perCanEl.querySelectorAll('[data-dp-qd-for]').forEach((el) => { if (el !== kept) el.remove(); });
        kept.removeAttribute('data-dp-qd-for');
        this._perCanKey = key;
        return;
      }

      const v = document.createElement('span');
      v.className = 'dp-percan__v';
      const price = document.createElement('span');
      price.className = 'product-price--original';
      price.textContent = this._formatMoney(eachCents);
      const unit = document.createElement('span');
      unit.className = 'dp-percan__unit';
      unit.textContent = this.perCanLabel;
      v.append(price, unit);
      if (refCents > eachCents) {
        const del = document.createElement('del');
        del.className = 'product-price--compare';
        del.textContent = this._formatMoney(refCents);
        v.append(del);
      }
      this.perCanEl.replaceChildren(v);
      this._perCanKey = key;
    }

    _formatMoney(cents) {
      const fmt = this.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const amountNoDec = Math.round(cents / 100).toString();
      const amountComma = amount.replace('.', ',');
      const amountNoDecComma = amountNoDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amountSpace = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecComma)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amountSpace)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'")) + this.isoSuffix;
    }
  }

  if (typeof customElements.get('card-qty-picker') === 'undefined') {
    customElements.define('card-qty-picker', CardQtyPicker);
  }
}
